import { Form } from '@epages/react-components'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type Immutable from 'immutable'
import cc from 'classcat'

import { previewThemeSettings, setThemeSettings } from '../../store/actions'
import { themeGroupDelimiter } from '../../../utils/themes'
import { useTranslate } from '../../utils/translate'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'
import themeSettingsFields from './themeSettingsFields'
import useThemeSettings from '../../utils/hooks/useThemeSettings'

// The "customStyle" top-level settings group has special handling:
// When its corresponding tab content is active, the settings layer becomes
// resizable via the Custom CSS field that is contained in this settings group.
// Furthermore, `CustomCssField` reports an explanatory text which is shown as
// info bubble tooltip in the settings layer's header.
const customStyleSettingsGroupName = 'customStyle'

// Workaround to exclude partial views from theme settings panel
const omitSettings = ['logoAdjustments', 'themeHeader', 'headerAnnouncement']

type Props = {
  active: boolean | string
  onToggle: (value?: { submit: boolean } | undefined) => void
}

export default function ThemeSettings({ active, onToggle }: Readonly<Props>) {
  const theme = useSelector<State, Core.Theme>((state) => state.getIn(['shop', 'theme']).toJS())
  const themeSettingsSchema = useSelector<State, Immutable.List<Immutable.Map<string, any>>>((state) =>
    state
      .get('themeSettingsSchema')
      .filter((themeSettingSchema) => !omitSettings.includes(themeSettingSchema.get('name'))),
  )
  const defaultThemeSettings = useSelector<State, State>((state) => state.get('defaultThemeSettings'))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))

  const [themeNameOrThemeGroupName] = theme.name.split(themeGroupDelimiter)
  const translationPrefix = `${themeNameOrThemeGroupName.replace('.', '_')}.settings`

  const t = useTranslate('interface')
  const tScoped = (key: string, ...args: any): string => t(`components.bottomBar.themeSettings.${key}`, args)
  const settingsT = (key: string): string => t(`themes:${translationPrefix}.${key}`)

  const dispatch = useDispatch<GlobalDispatch>()

  const themeSettings = useThemeSettings(omitSettings)
  const initialThemeSettings = useRef(themeSettings)
  const customCssHeaderTooltipText = useRef('')

  const [activeTab, setActiveTab] = useState(() =>
    typeof active === 'string' ? active : themeSettingsSchema.getIn([0, 'name']),
  )

  const iconClassname = cc([
    'ep-bottom-bar-button',
    'ep-bottom-bar-button-theme-settings',
    {
      checked: active,
    },
  ])

  useEffect(() => {
    initialThemeSettings.current = themeSettings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // Activate given tab in case of deep linking to it.
  useEffect(() => {
    if (typeof active === 'string') setActiveTab(active)
  }, [active])

  const onCancel = () => {
    onToggle()
  }

  const handleTabClick = (tabName) => {
    if (tabName !== activeTab) {
      setActiveTab(tabName)
    }
  }

  const onChange = (settings) => {
    dispatch(previewThemeSettings(settings))

    // Make sure not to return any data in order to prevent 'smart' data handling
    // https://github.com/ePages-de/react-components/blob/v0.4.3/src/form/Form.jsx#L142-L143
    return null
  }

  const onSubmit = async (settings: ImmutableMap) => {
    await dispatch(setThemeSettings(theme.id, settings))

    // Auto-checks the "Define your color palette" to-do in the BYD cockpit's dashboard checklist and setup guide
    if (!settings.get('colors').equals(initialThemeSettings.current.get('colors'))) {
      window.parent?.postMessage(
        {
          type: 'CHECKLIST',
          payload: {
            websiteOnly: 'designSettings.colorSettings',
            regularShop: 'designSettings.websiteCustomization',
          },
        },
        mboBaseUrl,
      )
    }

    // Auto-checks the "Customize your template" to-do in the BYD cockpit's dashboard checklist and setup guide
    if (!settings.get('fonts').equals(initialThemeSettings.current.get('fonts'))) {
      window.parent?.postMessage(
        {
          type: 'CHECKLIST',
          payload: {
            websiteOnly: 'designSettings.fontSettings',
            regularShop: 'designSettings.fontSettings',
          },
        },
        mboBaseUrl,
      )
    }

    onToggle({ submit: true })
  }

  const handleCustomCssInfoText = (text) => {
    customCssHeaderTooltipText.current = text
  }

  const settingFields = useMemo(() => {
    return {
      ...themeSettingsFields(settingsT, defaultThemeSettings, {
        onCustomCssInfoText: handleCustomCssInfoText,
      }),

      // Skip setting of type "style" (was only used internally)
      style: () => null,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <button className={iconClassname} title={tScoped('title')} onClick={() => onToggle()} />
      <SettingsLayer
        className={cc([
          'ep-settingslayer-theme-settings',
          {
            'custom-style-tab-active': activeTab === customStyleSettingsGroupName,
          },
        ])}
        onEscapeKeyDown={onCancel}
        open={!!active}
        side="top"
        align="center"
      >
        {({ renderLayout }) => {
          return (
            <Form
              className="ep-theme-settings-form"
              name="themeSettings"
              value={themeSettings}
              onChange={onChange}
              onSubmit={onSubmit}
            >
              {renderLayout({
                title: settingsT(activeTab),
                explanation: activeTab === customStyleSettingsGroupName ? customCssHeaderTooltipText.current : null,
                tabs: themeSettingsSchema
                  .map((setting) => {
                    const name = setting?.get('name')

                    return {
                      name,
                      title: settingsT(name),
                      active: activeTab === name,
                      onClick: (name: string) => handleTabClick(name),
                    }
                  })
                  .toJS(),
                body: themeSettingsSchema.map((setting) => {
                  const name = setting?.get('name')

                  return (
                    <Fragment key={name}>
                      <div
                        className={cc([
                          'dali-settingslayer-main-content-tab-content',
                          {
                            in: activeTab === name,
                            out: activeTab !== name,
                          },
                        ])}
                        data-testid={`theme-settings-content-${name}`}
                      >
                        {settingFields[setting?.get('type')]({
                          setting,
                          isTopLevel: true,
                        })}
                      </div>
                    </Fragment>
                  )
                }),
                footer: (
                  <ButtonRow>
                    <Button type="button" variant="cancel" onClick={onCancel}>
                      {tScoped('cancelButton.label')}
                    </Button>
                    <Button type="submit">{tScoped('applyButton.label')}</Button>
                  </ButtonRow>
                ),
              })}
            </Form>
          )
        }}
      </SettingsLayer>
    </>
  )
}
